import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CircleAlert } from "lucide-react";
import { api } from "../lib/api";

interface Props {
  noteId: string;
  onOpen: (id: string) => void;
}

export function BacklinksPanel({ noteId, onOpen }: Props) {
  const backlinks = useQuery({
    queryKey: ["backlinks", noteId],
    queryFn: () => api.listBacklinks(noteId),
  });
  const outgoing = useQuery({
    queryKey: ["outgoing", noteId],
    queryFn: () => api.listOutgoingLinks(noteId),
  });

  return (
    <div className="text-sm">
      <Section title="Backlinks" icon={<ArrowLeft className="h-4 w-4" />}>
        {backlinks.isLoading && <Loading />}
        {backlinks.data?.length === 0 && <Empty>Keine eingehenden Links</Empty>}
        <ul className="space-y-1">
          {backlinks.data?.map((b) => (
            <li key={b.source_note_id}>
              <button
                onClick={() => onOpen(b.source_note_id)}
                className="block w-full truncate rounded px-2 py-1 text-left hover:bg-zinc-900 hover:text-emerald-300"
              >
                {b.source_title}
                {b.anchor && <span className="text-zinc-500"> #{b.anchor}</span>}
              </button>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Outgoing" icon={<ArrowRight className="h-4 w-4" />}>
        {outgoing.isLoading && <Loading />}
        {outgoing.data?.length === 0 && <Empty>Keine ausgehenden Links</Empty>}
        <ul className="space-y-1">
          {outgoing.data?.map((l) => (
            <li key={l.id}>
              {l.target_note_id ? (
                <button
                  onClick={() => onOpen(l.target_note_id!)}
                  className="block w-full truncate rounded px-2 py-1 text-left hover:bg-zinc-900 hover:text-emerald-300"
                >
                  {l.target_title}
                  {l.anchor && <span className="text-zinc-500"> #{l.anchor}</span>}
                </button>
              ) : (
                <div
                  className="flex items-center gap-1.5 truncate rounded px-2 py-1 text-zinc-500"
                  title="Note existiert noch nicht — Wikilink ist dangling"
                >
                  <CircleAlert className="h-3 w-3 shrink-0 text-amber-500" />
                  <span className="truncate">{l.target_title}</span>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-zinc-800 p-3">
      <h3 className="mb-2 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500">
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
}

function Loading() {
  return <div className="text-xs text-zinc-500">Lade…</div>;
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-xs italic text-zinc-600">{children}</div>;
}
